import { useEffect, useState } from 'react';
import {
  Box, Button, Checkbox, Flex, FormControl, FormLabel, Grid, IconButton, Input, Modal, ModalBody, ModalCloseButton,
  ModalContent, ModalFooter, ModalHeader, ModalOverlay, Select, Text, useToast,
} from '@chakra-ui/react';
import { FiCheck, FiEdit2, FiX } from 'react-icons/fi';
import { createCategory, updateCategory, errorMessage } from 'api/administrador/finance';
import { Tag } from './ui';

const norm = (s) => String(s || '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

function Row({ category, canEdit, onChanged }) {
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(category.name);
  const run = async (body) => {
    try {
      await updateCategory(category.id, body);
      setEditing(false);
      onChanged();
    } catch (e) {
      toast({ status: 'error', title: errorMessage(e) });
    }
  };
  return (
    <Flex align="center" gap={2} py={2} borderBottom="1px solid" borderColor="gray.100" fontSize="sm">
      {editing ? (
        <>
          <Input size="sm" value={name} onChange={(e) => setName(e.target.value)} aria-label="Nuevo nombre" />
          <IconButton size="xs" aria-label="Guardar nombre" icon={<FiCheck />} onClick={() => run({ name })} />
          <IconButton size="xs" aria-label="Cancelar" icon={<FiX />} variant="ghost" onClick={() => { setName(category.name); setEditing(false); }} />
        </>
      ) : (
        <>
          <Text flex={1} minW={0} color={category.active ? 'gray.800' : 'gray.400'}>{category.name}</Text>
          {category.passThrough && <Tag bg="purple.100" color="purple.800">De paso</Tag>}
          {!category.active && <Tag>Inactiva</Tag>}
          {canEdit && (
            <>
              <IconButton size="xs" variant="ghost" aria-label={`Renombrar ${category.name}`} icon={<FiEdit2 />} onClick={() => setEditing(true)} />
              <Button size="xs" variant="ghost" colorScheme="purple" onClick={() => run({ active: !category.active })}>
                {category.active ? 'Desactivar' : 'Activar'}
              </Button>
            </>
          )}
        </>
      )}
    </Flex>
  );
}

/** Categorías de una vista (Bancos o Flujo de caja). No se borran: se desactivan. */
export default function CategoriesModal({ isOpen, onClose, book, categories, onChanged, canEdit, initialName, initialKind }) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [kind, setKind] = useState('egreso');
  const [passThrough, setPassThrough] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setName(initialName || '');
      // Si se abre desde un movimiento o un filtro, toma su tipo (ingreso o egreso).
      setKind(initialKind || 'egreso');
      setPassThrough(false);
    }
  }, [isOpen, initialName, initialKind]);

  const list = (categories || []).filter((c) => c.book === book);
  const groups = [
    { title: 'Egresos', items: list.filter((c) => c.kind === 'egreso' || c.kind === 'ambos') },
    { title: 'Ingresos', items: list.filter((c) => c.kind === 'ingreso' || c.kind === 'ambos') },
  ];

  async function add() {
    const twin = list.find((c) => norm(c.name) === norm(name));
    if (twin) {
      const where = twin.kind === 'ambos' ? 'ingresos y egresos' : `${twin.kind}s`;
      toast({ status: 'warning', title: `«${twin.name}» ya existe en ${where}${twin.active ? '' : ' (desactivada)'}.`, description: 'Búscala en la lista de abajo.' });
      return;
    }
    setSaving(true);
    try {
      await createCategory({ book, name, kind, passThrough });
      setName('');
      setPassThrough(false);
      toast({ status: 'success', title: 'Categoría creada', duration: 2500 });
      onChanged();
    } catch (e) {
      toast({ status: 'error', title: errorMessage(e) });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text fontSize="xl" fontWeight="600">Categorías de {book === 'bancos' ? 'Bancos' : 'Flujo de caja'}</Text>
          <Text fontSize="sm" color="gray.500" fontWeight="400">Solo aplican a esta vista. Las de la otra vista se manejan aparte.</Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {canEdit && (
            <Box p={4} mb={2} bg="purple.50" border="1px solid" borderColor="purple.100" borderRadius="lg">
              <Text fontSize="sm" fontWeight="600" color="purple.800" mb={2}>Nueva categoría</Text>
              <Grid templateColumns={{ base: '1fr', md: 'minmax(0,1fr) 170px auto' }} gap={2.5} alignItems="end">
                <FormControl>
                  <FormLabel htmlFor="cat-name" fontSize="sm">Nombre</FormLabel>
                  <Input id="cat-name" bg="white" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="cat-kind" fontSize="sm">Tipo</FormLabel>
                  <Select id="cat-kind" bg="white" value={kind} onChange={(e) => setKind(e.target.value)}>
                    <option value="egreso">Egreso</option>
                    <option value="ingreso">Ingreso</option>
                    <option value="ambos">Ingreso y egreso</option>
                  </Select>
                </FormControl>
                <Button bg="#574feb" color="white" _hover={{ bg: '#4a42d6' }} onClick={add} isLoading={saving} isDisabled={!name.trim()}>Agregar</Button>
              </Grid>
              <Checkbox mt={3} isChecked={passThrough} onChange={(e) => setPassThrough(e.target.checked)} colorScheme="purple" alignItems="flex-start">
                <Text fontSize="sm" fontWeight="600">Movimiento de paso</Text>
                <Text fontSize="sm" color="gray.500">Se ve en la vista y en el saldo, pero no suma en los ingresos ni en los egresos del Resumen. Ej.: cesantías.</Text>
              </Checkbox>
            </Box>
          )}
          {list.length === 0 ? (
            <Text fontSize="sm" color="gray.500" py={4}>Aún no hay categorías en esta vista.</Text>
          ) : (
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, minmax(0, 1fr))' }} gap={7}>
              {groups.map((g) => (
                <Box key={g.title}>
                  <Text fontSize="xs" fontWeight="700" letterSpacing="0.05em" textTransform="uppercase" color="gray.600" pt={3} pb={1}>
                    {g.title} · {g.items.length}
                  </Text>
                  {g.items.map((c) => (
                    <Row key={`${g.title}-${c.id}`} category={c} canEdit={canEdit} onChanged={onChanged} />
                  ))}
                </Box>
              ))}
            </Grid>
          )}
        </ModalBody>
        <ModalFooter justifyContent="space-between" gap={3}>
          <Text fontSize="sm" color="gray.500" maxW="420px">Una categoría con movimientos no se borra: se desactiva y deja de aparecer al registrar. Renombrarla actualiza sus movimientos.</Text>
          <Button onClick={onClose}>Cerrar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
